import { ComponentProps, FC, ReactNode } from "react";
import { Command as CommandPrimitive } from "cmdk";

import { I18nKeyType } from "@libs/i18n";
import { cn } from "@/utils/tailwind";

import {
  RmDialog,
  RmDialogContent,
  RmDialogDescription,
  RmDialogHeader,
  RmDialogTitle,
} from "./RmDialog";
import { RmSkeleton } from "./RmSkeleton";
import { textVariants } from "./RmText/variants";
import { BaseTextInput } from "./RmTextInput/BaseTextInput";

export const RmCommand: FC<ComponentProps<typeof CommandPrimitive>> = ({
  className,
  ...props
}) => {
  return (
    <CommandPrimitive
      data-slot="command"
      className={cn(
        "flex h-full w-full flex-col overflow-hidden rounded-md bg-popover text-popover-foreground",
        className,
      )}
      {...props}
    />
  );
};

type RmCommandDialogPropsType = ComponentProps<typeof RmDialog> & {
  titleI18nKey?: I18nKeyType;
  titleI18nOptions?: object;
  descriptionI18nKey?: I18nKeyType;
  descriptionI18nOptions?: object;
};

export const RmCommandDialog: FC<RmCommandDialogPropsType> = ({
  titleI18nKey,
  titleI18nOptions,
  descriptionI18nKey,
  descriptionI18nOptions,
  children,
  ...props
}) => {
  return (
    <RmDialog {...props}>
      <RmDialogHeader className="sr-only">
        {titleI18nKey && (
          <RmDialogTitle
            i18nKey={titleI18nKey}
            i18nOptions={titleI18nOptions}
          />
        )}
        {descriptionI18nKey && (
          <RmDialogDescription
            i18nKey={descriptionI18nKey}
            i18nOptions={descriptionI18nOptions}
          />
        )}
      </RmDialogHeader>
      <RmDialogContent
        className="overflow-hidden p-0"
        onPointerDownOutside={e => {
          if (
            e.target instanceof Element &&
            e.target.closest("[data-sonner-toast]")
          ) {
            e.preventDefault();
          }
        }}>
        <RmCommand className="**:data-[slot=command-input-wrapper]:h-12 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]]:px-2 [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5">
          {children}
        </RmCommand>
      </RmDialogContent>
    </RmDialog>
  );
};

type RmCommandInputPropsType = ComponentProps<typeof CommandPrimitive.Input> & {
  leftElement?: ReactNode | string;
  rightElement?: ReactNode | string;
  placeholderI18nKey?: I18nKeyType;
  placeholderI18nOptions?: object;
};

export const RmCommandInput: FC<RmCommandInputPropsType> = ({
  placeholderI18nKey,
  placeholderI18nOptions,
  onValueChange,
  ...props
}) => {
  return (
    <div data-slot="command-input-wrapper" className="flex flex-1 items-center">
      <CommandPrimitive.Input asChild data-slot="command-input">
        <BaseTextInput
          i18nKey={placeholderI18nKey}
          i18nOptions={placeholderI18nOptions}
          {...props}
          onChange={e => onValueChange?.(e.target.value)}
        />
      </CommandPrimitive.Input>
    </div>
  );
};

export const RmCommandList: FC<
  ComponentProps<typeof CommandPrimitive.List>
> = ({ className, ...props }) => {
  return (
    <CommandPrimitive.List
      data-slot="command-list"
      className={cn(
        "max-h-[300px] scroll-py-1 overflow-x-hidden overflow-y-auto",
        className,
      )}
      {...props}
    />
  );
};

export const RmCommandEmpty: FC<
  ComponentProps<typeof CommandPrimitive.Empty>
> = ({ className, ...props }) => {
  return (
    <CommandPrimitive.Empty
      data-slot="command-empty"
      className={cn("pt-4 pb-6 text-center", textVariants(), className)}
      {...props}
    />
  );
};

export const RmCommandGroup: FC<
  ComponentProps<typeof CommandPrimitive.Group>
> = ({ className, ...props }) => {
  return (
    <CommandPrimitive.Group
      data-slot="command-group"
      className={cn(
        "overflow-hidden p-1 text-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground",
        className,
      )}
      {...props}
    />
  );
};

export const RmCommandSeparator: FC<
  ComponentProps<typeof CommandPrimitive.Separator>
> = ({ className, ...props }) => {
  return (
    <CommandPrimitive.Separator
      data-slot="command-separator"
      className={cn("-mx-1 h-px bg-border", className)}
      {...props}
    />
  );
};

export const RmCommandItem: FC<
  ComponentProps<typeof CommandPrimitive.Item>
> = ({ className, ...props }) => {
  return (
    <CommandPrimitive.Item
      data-slot="command-item"
      className={cn(
        "relative flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 outline-hidden select-none data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50 data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 [&_svg:not([class*='text-'])]:text-muted-foreground",
        textVariants(),
        className,
      )}
      {...props}
    />
  );
};

export const RmCommandShortcut: FC<ComponentProps<"span">> = ({
  className,
  ...props
}) => {
  return (
    <span
      data-slot="command-shortcut"
      className={cn(
        textVariants({ variant: "sm" }),
        "ml-auto tracking-widest text-muted-foreground",
        className,
      )}
      {...props}
    />
  );
};

export const RmCommandLoading = () => {
  return (
    <CommandPrimitive.Loading>
      <div className="p-1">
        <RmSkeleton className="h-8 w-full" />
      </div>
    </CommandPrimitive.Loading>
  );
};
